import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Tag } from "@/components/ui/tag"
import { HealthDataCardSession } from "@/modules/patients/detail/health-information"
import { GynaecologyDrawer } from "@/modules/patients/detail/health-information/gynaecology/gynaecology-drawer"
import { useGetHealthInformationVerification } from "@/modules/patients/detail/health-information/hooks/useGetHealthInformationVerification"
import { useHealthInformationInContextAppointment } from "@/modules/patients/detail/health-information/hooks/useHealthInformationCanVerify"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { GynaecologyFragment$key } from "@/modules/patients/services/__generated__/GynaecologyFragment.graphql"
import { GynaecologyFragment } from "@/modules/patients/services/GynaecologyFragment"
import { usePatientGynaecology } from "@/modules/patients/services/usePatientGynaecology"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Country } from "@/types/__generated__/union-types"

interface GynaecologyBodyProps {
  country: Country
  patientId: string
}

const Gynaecology = ({ country, patientId }: GynaecologyBodyProps) => {
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const appointmentId = useAppointmentParam()
  const { t } = useTranslation()

  const { verifiedBy, verifiedAt, isVerified } = useGetHealthInformationVerification(patientId, country, "GYNAECOLOGY")

  const validAppointment = useHealthInformationInContextAppointment()
  const canVerify = !isVerified && validAppointment

  const dataRef = usePatientGynaecology(patientId, country)
  const data = useFragment<GynaecologyFragment$key>(GynaecologyFragment, dataRef)
  const currentYear = new Date().getFullYear()

  const conditionCardBody = () => {
    if (data?.menopauseSince && !data.weeksOfPregnancy && currentYear - data?.menopauseSince > 1) {
      return (
        <div className="flex max-w-full flex-wrap items-center gap-2">
          <Tag
            className="truncate"
            type="neutral"
            label={`${t("patient.healthInformation.gynaecology.form.menopauseSince")} ${data.menopauseSince}`}
          />
        </div>
      )
    }

    const keyWeekTranslation =
      data?.weeksOfPregnancy === 1
        ? "patient.healthInformation.gynaecology.form.tag.keyWeek_one"
        : "patient.healthInformation.gynaecology.form.tag.keyWeek_other"

    switch (data?.pregnancy) {
      case "PREGNANT":
        return (
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <Tag
              className="truncate"
              type="neutral"
              label={`${t("patient.healthInformation.gynaecology.form.options.PREGNANT") + ": " + String.fromCharCode(0x2248)}  ${data.weeksOfPregnancy && t(keyWeekTranslation, { count: data.weeksOfPregnancy })}`}
            />
          </div>
        )
      case "NOT_PREGNANT":
        return (
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <Tag
              className="truncate"
              type="neutral"
              label={`${t("patient.healthInformation.gynaecology.form.options.NOT_PREGNANT")}`}
            />
          </div>
        )
      case "UNKNOWN":
        return null
    }
  }

  return (
    <HealthDataCardSession
      sectionName="gynaecology"
      analyticsSectionName="healthInformation_gynaecology"
      disabled={drawerName === "GynaecologyDrawer"}
      canVerify={canVerify}
      title={t("patient.healthInformation.gynaecology.title")}
      editHandler={() => {
        openDrawer(
          () => (
            <GynaecologyDrawer
              canVerify={canVerify}
              verifiedBy={verifiedBy ?? ""}
              verifiedAt={verifiedAt ?? ""}
              appointmentId={appointmentId}
              data={data}
            />
          ),
          "GynaecologyDrawer",
        )
      }}>
      {conditionCardBody()}
    </HealthDataCardSession>
  )
}

export { Gynaecology }
